import React, { useState } from 'react';
import cn from 'classnames';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { UiKit, ProtectedLink } from 'components';
import { useRequestAction } from 'views/Requests/hooks';

/** @type {Record<string, import('utils/types').ButtonVariant>} */
const BUTTON_TYPE_VARIANT_MAP = {
  'admin-approval': 'secondary',
  cancel: 'destructive'
};

export default function RequestAction({
  data = {},
  retexes = [],
  isCompact = false,
  isDisabled = false,
  isApproving = true,
  isRefreshing = false,
  onBeforeProcessing = () => {},
  onSetProcessingId = () => {},
  onRetexUpdate = () => {},
  onAfterProcessing = () => {},
  className = ''
}) {
  const {
    show,
    text,
    type,
    disabled,
    isProcessing,
    tooltipMessage,
    alertModalData,
    onStartProcess
  } = useRequestAction(
    data,
    retexes,
    isCompact,
    isApproving,
    onBeforeProcessing,
    onSetProcessingId,
    onRetexUpdate,
    onAfterProcessing
  );
  const [showAlert, setShowAlert] = useState();

  const id = _get(data, 'id', null);
  const elementId = `button-${id}`;
  const isButtonDisabled =
    disabled || isDisabled || isProcessing || isRefreshing;

  const onClick = event => {
    event.stopPropagation();
    if (isButtonDisabled) return;
    if (alertModalData) return setShowAlert(true);
    onStartProcess();
  };

  const onCloseAlertModal = () => setShowAlert(false);

  const alertModalTitle = _get(alertModalData, 'title', '');
  const alertModalDescription = _get(alertModalData, 'description', '');
  const alertModalButtonText = _get(alertModalData, 'buttonText', '');
  const buttonVariant = isApproving
    ? BUTTON_TYPE_VARIANT_MAP[type] || 'outline'
    : 'outline';

  if (!id || !show) return null;

  return (
    <>
      <ProtectedLink
        disabled
        id={elementId}
        tooltipAutohide={false}
        className={cn(styles.container, className)}
        hideTooltip={!tooltipMessage}
        tooltipMessage={tooltipMessage}
        tooltipInnerClassName={styles.tooltip}
        tooltipDelay={{ show: 100, hide: 100 }}
      >
        <UiKit.Button
          size={isCompact ? 'md' : 'lg'}
          variant={buttonVariant}
          onClick={onClick}
          processing={isProcessing}
          disabled={isButtonDisabled}
          className={styles.button}
          data-type={type}
          data-approval={isApproving}
        >
          {isRefreshing ? 'Verifying...' : text}
        </UiKit.Button>
      </ProtectedLink>
      <UiKit.Modal
        isOpen={showAlert}
        onClose={onCloseAlertModal}
        isProcessing={isProcessing}
        className={styles.alertModal}
      >
        <UiKit.Modal.Header title={alertModalTitle} />
        <UiKit.Modal.Body>
          <p className={styles.alertDescription}>{alertModalDescription}</p>
        </UiKit.Modal.Body>
        <UiKit.Modal.Footer
          onConfirm={onStartProcess}
          confirmBtnVariant="destructive"
          confirmBtnText={alertModalButtonText}
        />
      </UiKit.Modal>
    </>
  );
}
